import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  getDashboardStats, getIncidents, getRisks, getAudits, getSPIs, 
  getComplianceStatus
} from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, FileWarning, AlertTriangle, ClipboardCheck, BarChart3,
  Download, Eye, Calendar, Building2, CheckCircle, XCircle, Clock,
  TrendingUp, TrendingDown, Minus, FileText, Lock, LogOut, Languages, Sun, Moon
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const SEVERITY_COLORS = {
  catastrophic: 'bg-red-600 text-white',
  hazardous: 'bg-orange-600 text-white',
  major: 'bg-yellow-600 text-white',
  minor: 'bg-blue-600 text-white',
  negligible: 'bg-slate-600 text-white',
};

const RISK_COLORS = {
  critical: 'bg-red-600',
  high: 'bg-orange-600',
  medium: 'bg-yellow-600',
  low: 'bg-emerald-600',
};

const AUDIT_STATUS_COLORS = {
  planned: 'bg-blue-900/30 text-blue-400',
  in_progress: 'bg-yellow-900/30 text-yellow-400',
  completed: 'bg-emerald-900/30 text-emerald-400',
  cancelled: 'bg-slate-800 text-slate-400',
};

export default function InspectorPortal() {
  const { t } = useTranslation();
  const { api, user, logout } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const { toggleTheme, isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [risks, setRisks] = useState([]);
  const [audits, setAudits] = useState([]);
  const [spis, setSPIs] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, incidentsRes, risksRes, auditsRes, spisRes, complianceRes] = await Promise.all([
        getDashboardStats(api),
        getIncidents(api, { limit: 50 }),
        getRisks(api, { limit: 50 }),
        getAudits(api, { limit: 50 }),
        getSPIs(api, { limit: 50 }),
        getComplianceStatus(api)
      ]);
      setStats(statsRes.data);
      setIncidents(incidentsRes.data || []);
      setRisks(risksRes.data || []);
      setAudits(auditsRes.data || []);
      setSPIs(spisRes.data || []);
      setCompliance(complianceRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load inspector data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (reportType) => {
    setDownloadingReport(reportType);
    try {
      let endpoint;
      switch (reportType) {
        case 'incidents':
          // Get the first incident for demonstration, or generate a summary
          endpoint = '/reports/risks/pdf'; // Use risks as proxy for now
          break;
        case 'risk_register':
          endpoint = '/reports/risks/pdf';
          break;
        case 'audits':
          // Would need specific audit ID - use compliance as fallback
          endpoint = '/reports/compliance/gaca/pdf';
          break;
        case 'spis':
          endpoint = '/reports/spis/pdf';
          break;
        case 'compliance':
          endpoint = '/reports/compliance/gaca/pdf';
          break;
        default:
          endpoint = '/reports/risks/pdf';
      }
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(null);
    }
  };

  // Calculate compliance percentage
  const compliancePercentage = compliance ? 
    Math.round((compliance.compliant_count / Math.max(compliance.total_clauses, 1)) * 100) : 0;

  // Risk distribution
  const criticalRisks = risks.filter(r => r.risk_level === 'critical').length;
  const highRisks = risks.filter(r => r.risk_level === 'high').length;
  const openIncidents = incidents.filter(i => i.status === 'open' || i.status === 'under_investigation').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4" />
          <p className="text-slate-400">Loading Inspector Portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-lg bg-emerald-600/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="inspector-portal-title">
                GACA Inspector Portal
              </h1>
              <p className="text-slate-400">Saudi General Authority of Civil Aviation - Safety Oversight</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-600/20 text-emerald-400 border border-emerald-600/50 px-3 py-1">
            <Lock className="w-3 h-3 mr-1" />
            Read-Only Access
          </Badge>
          <Badge variant="outline" className="border-slate-600 text-slate-400">
            {user?.name || user?.email || 'Inspector'}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => changeLanguage(language === 'ar' ? 'en' : 'ar')}
            className="text-slate-300 hover:text-white"
            data-testid="inspector-portal-language-toggle"
            aria-label="Toggle language"
            title={language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
          >
            <Languages className="w-4 h-4 mr-1.5" />
            {language === 'ar' ? 'EN' : 'AR'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="text-slate-300 hover:text-white"
            data-testid="inspector-portal-theme-toggle"
            aria-label="Toggle theme"
            title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
            data-testid="inspector-portal-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            {t('common.logout', 'Sign Out')}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Open Incidents</p>
                <p className={cn("text-2xl font-bold", openIncidents > 0 ? "text-red-400" : "text-emerald-400")}>
                  {openIncidents}
                </p>
              </div>
              <FileWarning className={cn("w-8 h-8", openIncidents > 0 ? "text-red-400" : "text-emerald-400")} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Critical Risks</p>
                <p className={cn("text-2xl font-bold", criticalRisks > 0 ? "text-red-400" : "text-emerald-400")}>
                  {criticalRisks}
                </p>
              </div>
              <AlertTriangle className={cn("w-8 h-8", criticalRisks > 0 ? "text-red-400" : "text-emerald-400")} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">High Risks</p>
                <p className={cn("text-2xl font-bold", highRisks > 0 ? "text-orange-400" : "text-emerald-400")}>
                  {highRisks}
                </p>
              </div>
              <AlertTriangle className={cn("w-8 h-8", highRisks > 0 ? "text-orange-400" : "text-emerald-400")} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Compliance</p>
                <p className={cn("text-2xl font-bold", compliancePercentage >= 80 ? "text-emerald-400" : "text-yellow-400")}>
                  {compliancePercentage}%
                </p>
              </div>
              <Building2 className={cn("w-8 h-8", compliancePercentage >= 80 ? "text-emerald-400" : "text-yellow-400")} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active SPIs</p>
                <p className="text-2xl font-bold text-sky-400">{spis.filter(s => s.is_active).length}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-sky-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Reports Section */}
      <Card className="bg-slate-900/60 border-slate-800 border-l-4 border-l-emerald-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-emerald-400" />
            Export Reports for Inspection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {[
              { type: 'incidents', label: 'Incidents Report', icon: FileWarning },
              { type: 'risk_register', label: 'Risk Register', icon: AlertTriangle },
              { type: 'audits', label: 'Audit Report', icon: ClipboardCheck },
              { type: 'spis', label: 'SPIs Dashboard', icon: BarChart3 },
              { type: 'compliance', label: 'Compliance Status', icon: Building2 },
            ].map(({ type, label, icon: Icon }) => (
              <Button
                key={type}
                variant="outline"
                onClick={() => handleDownloadReport(type)}
                disabled={downloadingReport === type}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid={`download-${type}-btn`}
              >
                {downloadingReport === type ? (
                  <div className="spinner w-4 h-4 mr-2" />
                ) : (
                  <Icon className="w-4 h-4 mr-2" />
                )}
                {label}
                <Download className="w-3 h-3 ml-2" />
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Tabs */}
      <Tabs defaultValue="compliance" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="compliance" className="data-[state=active]:bg-emerald-600">
            <Building2 className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="incidents" className="data-[state=active]:bg-emerald-600">
            <FileWarning className="w-4 h-4 mr-2" />
            Incidents
          </TabsTrigger>
          <TabsTrigger value="risks" className="data-[state=active]:bg-emerald-600">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Risks
          </TabsTrigger>
          <TabsTrigger value="audits" className="data-[state=active]:bg-emerald-600">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Audits
          </TabsTrigger>
          <TabsTrigger value="spis" className="data-[state=active]:bg-emerald-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            SPIs
          </TabsTrigger>
        </TabsList>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">GACA Regulatory Compliance Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Overall Compliance */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-300">Overall Compliance</span>
                    <span className={cn("text-xl font-bold", compliancePercentage >= 80 ? "text-emerald-400" : "text-yellow-400")}>
                      {compliancePercentage}%
                    </span>
                  </div>
                  <Progress value={compliancePercentage} className="h-3 bg-slate-700" />
                  <div className="flex justify-between mt-2 text-sm text-slate-500">
                    <span>{compliance?.compliant_count || 0} compliant</span>
                    <span>{compliance?.total_clauses || 0} total clauses</span>
                  </div>
                </div>

                {/* SMS Components */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'policy', label: 'Safety Policy', icon: FileText },
                    { key: 'risk_management', label: 'Risk Management', icon: AlertTriangle },
                    { key: 'assurance', label: 'Safety Assurance', icon: ClipboardCheck },
                    { key: 'promotion', label: 'Safety Promotion', icon: BarChart3 },
                  ].map(({ key, label, icon: Icon }) => {
                    const component = compliance?.by_component?.[key] || { compliant: 0, total: 0 };
                    const pct = component.total > 0 ? Math.round((component.compliant / component.total) * 100) : 0;
                    return (
                      <div key={key} className="p-4 bg-slate-800/30 rounded-lg text-center">
                        <Icon className={cn("w-8 h-8 mx-auto mb-2", pct >= 80 ? "text-emerald-400" : "text-yellow-400")} />
                        <p className="text-sm text-slate-400 mb-1">{label}</p>
                        <p className={cn("text-2xl font-bold", pct >= 80 ? "text-emerald-400" : "text-yellow-400")}>{pct}%</p>
                        <p className="text-xs text-slate-500">{component.compliant}/{component.total}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Incident Register ({incidents.length} records)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">ID</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Title</TableHead>
                      <TableHead className="text-slate-400">Category</TableHead>
                      <TableHead className="text-slate-400">Severity</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incidents.slice(0, 20).map((incident) => (
                      <TableRow key={incident.id} className="border-slate-800">
                        <TableCell className="font-mono text-sky-400">{incident.incident_number}</TableCell>
                        <TableCell className="text-slate-300">
                          {incident.occurrence_date ? format(new Date(incident.occurrence_date), 'PP') : 'N/A'}
                        </TableCell>
                        <TableCell className="text-white max-w-[200px] truncate">{incident.title}</TableCell>
                        <TableCell className="text-slate-400 capitalize">{incident.category?.replace('_', ' ')}</TableCell>
                        <TableCell>
                          <Badge className={SEVERITY_COLORS[incident.severity] || 'bg-slate-600'}>
                            {incident.severity}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn(
                            "border",
                            incident.status === 'closed' ? "border-emerald-600 text-emerald-400" :
                            incident.status === 'open' ? "border-red-600 text-red-400" :
                            "border-yellow-600 text-yellow-400"
                          )}>
                            {incident.status?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {incidents.length > 20 && (
                <p className="text-center text-slate-500 mt-4">Showing 20 of {incidents.length} incidents. Download full report for complete data.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="space-y-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Risk Register ({risks.length} records)</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Risk Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {['critical', 'high', 'medium', 'low'].map(level => {
                  const count = risks.filter(r => r.risk_level === level).length;
                  return (
                    <div key={level} className="p-3 bg-slate-800/50 rounded-lg text-center">
                      <div className={cn("w-4 h-4 rounded-full mx-auto mb-2", RISK_COLORS[level])} />
                      <p className="text-2xl font-bold text-white">{count}</p>
                      <p className="text-xs text-slate-400 capitalize">{level}</p>
                    </div>
                  );
                })}
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">ID</TableHead>
                      <TableHead className="text-slate-400">Title</TableHead>
                      <TableHead className="text-slate-400">Initial Risk</TableHead>
                      <TableHead className="text-slate-400">Residual Risk</TableHead>
                      <TableHead className="text-slate-400">Level</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {risks.slice(0, 20).map((risk) => (
                      <TableRow key={risk.id} className="border-slate-800">
                        <TableCell className="font-mono text-sky-400">{risk.risk_number}</TableCell>
                        <TableCell className="text-white max-w-[200px] truncate">{risk.title}</TableCell>
                        <TableCell className="text-slate-400">
                          {risk.severity || risk.initial_severity || 'N/A'} × {risk.likelihood || risk.initial_likelihood || 'N/A'}
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {risk.residual_severity || risk.severity || 'N/A'} × {risk.residual_likelihood || risk.likelihood || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(RISK_COLORS[risk.risk_level] || 'bg-slate-600', "text-white")}>
                            {risk.risk_level || 'unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn(
                            "border",
                            risk.status === 'closed' ? "border-emerald-600 text-emerald-400" :
                            risk.status === 'open' ? "border-blue-600 text-blue-400" :
                            "border-yellow-600 text-yellow-400"
                          )}>
                            {risk.status || 'active'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audits Tab */}
        <TabsContent value="audits" className="space-y-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Audit Program ({audits.length} audits)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">Audit #</TableHead>
                      <TableHead className="text-slate-400">Title</TableHead>
                      <TableHead className="text-slate-400">Type</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Findings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audits.slice(0, 20).map((audit) => {
                      const majorFindings = audit.findings?.filter(f => f.severity === 'major').length || 0;
                      const minorFindings = audit.findings?.filter(f => f.severity === 'minor').length || 0;
                      return (
                        <TableRow key={audit.id} className="border-slate-800">
                          <TableCell className="font-mono text-sky-400">{audit.audit_number}</TableCell>
                          <TableCell className="text-white max-w-[200px] truncate">{audit.title}</TableCell>
                          <TableCell className="text-slate-400 capitalize">{audit.audit_type}</TableCell>
                          <TableCell className="text-slate-300">
                            {audit.planned_date ? format(new Date(audit.planned_date), 'PP') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge className={AUDIT_STATUS_COLORS[audit.status]}>
                              {audit.status?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {majorFindings > 0 && (
                                <Badge className="bg-red-600 text-white">{majorFindings} major</Badge>
                              )}
                              {minorFindings > 0 && (
                                <Badge className="bg-orange-600 text-white">{minorFindings} minor</Badge>
                              )}
                              {majorFindings === 0 && minorFindings === 0 && (
                                <span className="text-slate-500">-</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SPIs Tab */}
        <TabsContent value="spis" className="space-y-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Safety Performance Indicators ({spis.length} SPIs)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {spis.filter(s => s.is_active).map((spi) => {
                  const currentValue = spi.current_value ?? 0;
                  const targetValue = spi.target_value ?? 1;
                  const alertThreshold = spi.alert_threshold ?? 0;
                  const isAlert = currentValue >= alertThreshold;
                  const progressValue = Math.min((currentValue / targetValue) * 100, 100);
                  const TrendIcon = spi.trend === 'improving' ? TrendingDown : spi.trend === 'declining' ? TrendingUp : Minus;
                  const trendColor = spi.trend === 'improving' ? 'text-emerald-400' : spi.trend === 'declining' ? 'text-red-400' : 'text-slate-400';

                  return (
                    <div key={spi.id} className={cn(
                      "p-4 bg-slate-800/50 rounded-lg border",
                      isAlert ? "border-red-500" : "border-slate-700"
                    )}>
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-mono text-xs text-sky-400">{spi.spi_number}</span>
                        <div className={cn("flex items-center gap-1 text-xs", trendColor)}>
                          <TrendIcon className="w-3 h-3" />
                          {spi.trend || 'stable'}
                        </div>
                      </div>
                      <h4 className="text-white font-medium mb-2">{spi.name}</h4>
                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <p className={cn("text-2xl font-bold", isAlert ? "text-red-400" : "text-white")}>
                            {currentValue.toFixed(1)}
                          </p>
                          <p className="text-xs text-slate-500">{spi.unit || ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-400">Target: {targetValue}</p>
                          <p className="text-xs text-slate-500">Threshold: {alertThreshold}</p>
                        </div>
                      </div>
                      <Progress value={progressValue} className="h-2 bg-slate-700" />
                      {isAlert && (
                        <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Above alert threshold
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="text-center text-slate-500 text-sm py-4 border-t border-slate-800">
        <p>This portal provides read-only access for regulatory inspection purposes.</p>
        <p>Data as of: {format(new Date(), 'PPpp')}</p>
      </div>
    </div>
  );
}
